import React from 'react';
import { Link as reactRouterLink, Route, Switch } from 'react-router-dom';
import Head from 'react-helmet';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import { PageFooter } from '@zillow/page-frame';
import PaymentsHeaderNav from '@common/components/PaymentsHeaderNav';
import ErrorBoundary from '@common/components/ErrorBoundary';
import ImpersonationNav from '@common/components/ImpersonationNav';
import PropertyDetailsPage from '@rental-manager/pages/PropertyDetailsPage';
import HomePage from '@rental-manager/pages/HomePage';
import browserUtils from '@common/utils/browserUtils';
import userUtils from '@common/utils/userUtils';
import paths from '@rental-manager/pathsManifest';
import './index.scss';
import type { IClusterSettingsModel, ICookieEnabledServiceModel, ILocationModel } from '@rental-manager/models/Types';
import styled from 'styled-components';
import { token } from '@zillow/constellation';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { onPageFrameLinkClick } from '@zg-rentals/datagrail';

const HELPER_LINK_PARAM = 'ticket_form_id=360000053487';
const logger = getGlobalLogger('landlord/component');

const StyledFooterContainer = styled.div`
  background-color: ${token('colors.white')};
  /* Investigate why the zrs-comet Footer doesn't work as is */
  ul {
    columns: 1;
    column-width: auto;
  }
  li {
    display: inline-block;
    margin: 0px 10px;
  }
`;

interface AppProps {
  user: { [key: string]: any };
  isMobile: boolean;
  cookieEnabledServices: ICookieEnabledServiceModel;
  clusterSettings: IClusterSettingsModel;
  pathname: string;
  queryString: string;
  brand: string;
  location: ILocationModel;
  encodedGuidZuid: string;
  handleResize: (...args: Array<any>) => any;
}
interface AppState {
  isResizing: boolean;
}
class App extends React.Component<AppProps, AppState> {
  state = {
    isResizing: false,
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.handleThrottledResize();

    const { cookieEnabledServices } = this.props;
    const { hostApp } = cookieEnabledServices;
    if (hostApp !== 'android') {
      window.addEventListener('resize', this.handleThrottledResize);
    }

    logger?.info('app mounted');
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleThrottledResize);
  }

  handleThrottledResize = () => {
    const { isResizing } = this.state;

    if (isResizing) {
      return;
    }

    return (
      Promise.resolve()
        // lock the resizing flag
        .then(() => {
          this.setState({ isResizing: true });
        })
        // wait for 1 second
        .then(
          () =>
            new Promise((resolve) => {
              setTimeout(resolve, 1000);
            }),
        )
        // calculate the new screen size
        .then(() => this.props.handleResize(browserUtils.screen.width()))
        // unlock the resizing flag
        .then(() => {
          this.setState({ isResizing: false });
        })
    );
  };

  handleLogoutClick = (): void => {
    window.location.href = '/Logout.htm';
  };

  handleStopImpersonatingClick = (): void => {
    window.location.href = paths.auth.stopImpersonatingPage;
  };

  renderRoutes = ({ isInWebView }): React.ReactNode => (
    <Switch>
      <Route
        path={'/rental-manager/payments/properties/:propertyId'}
        render={(routeProps) => (
          <ErrorBoundary>
            <PropertyDetailsPage {...routeProps} isInWebView={isInWebView}>
              {({ listing, clusterSettings }) => (
                <ErrorBoundary>
                  <HomePage
                    baseUrl={`/rental-manager/payments/properties/${listing.propertyId}`}
                    listing={listing}
                    propertyId={listing.propertyId}
                    clusterSettings={clusterSettings}
                    location={this.props.location}
                  />
                </ErrorBoundary>
              )}
            </PropertyDetailsPage>
          </ErrorBoundary>
        )}
      />
    </Switch>
  );

  render(): React.ReactNode {
    const { user, cookieEnabledServices, pathname, brand } = this.props;
    const { email, impersonatorEmail, impersonatorUserToken, roles } = user;
    const { hostApp } = cookieEnabledServices;
    const isInWebView = hostApp === 'android' || hostApp === 'ios';
    const isImpersonator = impersonatorEmail && impersonatorUserToken && userUtils.isPostingPathAdmin(roles);
    return (
      <ErrorBoundary>
        <StyledContainer backgroundColor="zrmBackground" id="App">
          <Head>
            <title>Rental Manager | Payments</title>
          </Head>
          {isImpersonator && (
            <ImpersonationNav emailAddress={email} handleStopImpersonatingClick={this.handleStopImpersonatingClick} />
          )}
          <div className="App-Body-Wrapper">
            <PaymentsHeaderNav
              brand={brand}
              userModel={user}
              useRelativePaths={false}
              reactRouterLink={reactRouterLink}
              pathname={pathname}
              hideHeaderNavigation={isInWebView}
              handleLogoutClick={this.handleLogoutClick}
            >
              {this.renderRoutes({ isInWebView })}
              {!isInWebView && (
                <StyledFooterContainer>
                  <PageFooter onLinkClick={onPageFrameLinkClick} />
                </StyledFooterContainer>
              )}
            </PaymentsHeaderNav>
          </div>
        </StyledContainer>
      </ErrorBoundary>
    );
  }
}

export default App;
